import React, {
  useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import PropTypes from 'prop-types';
import { Col, Container, Row } from 'react-bootstrap';
import { faBan, faStar } from '@fortawesome/free-solid-svg-icons';
import { toast } from 'react-toastify';
import { matchSorter } from 'match-sorter';
import { useParams } from 'react-router-dom';
import { CiatAppContext, WinManagerContext } from '../../providers';
import { REPORTS_API_URL } from '../basicReport/hooks/consts';
import api from '../../api/req';
import { Loader, SearchInput } from '../../components/bootStrap';
import ReportsList from './list';
import IconAlert from '../../components/blanks/common/IconAlert';
import PageHeader from '../../components/blanks/common/PageHeader';
import Labels from './labels';
import { RefreshButton } from '../../components/bootStrap/buttons/refreshButton';
import FalconCloseButton from '../../components/blanks/common/CloseButton';

function getChildrens(itm) {
  function flatInnter(itms) {
    return itms.reduce((R, child) => {
      if (child.children) {
        return [...R, child, ...flatInnter(child.children)];
      }
      return [...R, child];
    }, []);
  }
  if (itm.children) {
    return flatInnter(itm.children);
  }
  return [];
}

const FAVORITES_LABEL_ID = -1;
const UNSORTED_LABEL_ID = -2;

function ReportsPanel({ schemaId }) {
  const { auth, currentUser } = useContext(CiatAppContext);
  const { dellComponentFromWindowsManager } = useContext(WinManagerContext);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [reports, setReports] = useState([]);
  const [labels, setLabels] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [search, setSearch] = useState('');
  const [currentLabel, setCurrentLabel] = useState(FAVORITES_LABEL_ID);
  const { reportName } = useParams();

  // const [ordering, setOrdering] = useState('name');
  const ordering = 'name';
  // const [is_active, setIsActive] = useState('true');
  const isActive = true;
  // const [deleted, setDeleted] = useState('false');
  const deleted = false;

  const loadReports = useCallback(() => {
    const reportsLoader = async () => {
      const searchOptions = {
        ordering, isActive, deleted,
      };
      if (schemaId) searchOptions.schema__identifier = schemaId;
      const r = await api.get(REPORTS_API_URL, auth, searchOptions);
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      return r.json();
    };
    const labelsLoader = async () => {
      const r = await api.get('/api/reports/report-categories/', auth);
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      return r.json();
    };
    const favoritesLoader = async () => {
      const r = await api.get('/api/reports/favorites/', auth);
      if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
      return r.json();
    };
    const loader = async () => {
      const result = await Promise.all([reportsLoader, labelsLoader, favoritesLoader]);
      const e = result
        .filter((r) => r.status === 'rejected')
        .map((r) => r.reason).join(', ');
      if (e) throw new Error(e);
      return Promise.all(result.map((r) => r()));
    };

    setLoading(true);
    loader()
      .then((result) => {
        setReports(result[0]);
        setLabels(result[1]);
        setFavorites(result[2]);
      })
      .catch((e) => setErr(e.message))
      .finally(() => {
        setLoading(false);
      });
  }, [auth, deleted, isActive, schemaId]);

  useEffect(() => loadReports(), [auth, deleted, isActive, loadReports, schemaId]);

  const onSearchHandler = useCallback(
    (e, v) => {
      setSearch(v);
    },
    [],
  );

  const displayLabels = useMemo(
    () => [
      {
        id: FAVORITES_LABEL_ID, name: 'Улюблені', faIcon: faStar, iconClass: 'text-warning',
      },
      ...(currentUser.is_superuser ? [
        {
          id: UNSORTED_LABEL_ID, name: 'Несортовані', faIcon: faBan, iconClass: 'text-danger',
        },
      ] : []),
      ...labels,

    ],
    [currentUser.is_superuser, labels],
  );

  const displayReports = useMemo(
    () => {
      if (schemaId) return reports;
      if (reportName) {
        return reports.filter((r) => r.identifier === reportName);
      }
      if (currentLabel === FAVORITES_LABEL_ID) {
        return reports.filter((r) => favorites.includes(r.id));
      }
      const flatLabels = labels.reduce((R, l) => [...R, l, ...getChildrens(l)], []);
      if (currentLabel === UNSORTED_LABEL_ID) {
        const sortedReportsIds = new Set(flatLabels.reduce((R, l) => [...R, ...l.reports], []));
        return reports.filter((r) => !sortedReportsIds.has(r.id));
      }
      const cl = flatLabels.filter((l) => l.id === currentLabel).reduce((R, l) => l, null);
      if (!cl) return [];
      const cll = [cl, ...getChildrens(cl)];
      const reportsIds = new Set(cll.reduce((R, l) => [...R, ...l.reports], []));
      return reports.filter((r) => reportsIds.has(r.id));
    },
    [currentLabel, favorites, labels, reportName, reports, schemaId],
  );

  const displayReport2 = useMemo(
    () => matchSorter(displayReports, search, { keys: ['name', 'description'] }),
    [displayReports, search],
  );

  const onToggleFavorites = useCallback(
    (reportId) => {
      const onAdd = async () => {
        const r = await api.put(`/api/reports/favorites/${reportId}/`, auth);
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return true;
      };
      const onRem = async () => {
        const r = await api.delete(`/api/reports/favorites/${reportId}/`, auth);
        if (!r.ok) throw new Error(`${r.status} ${r.statusText}`);
        return true;
      };
      const curRep = reports.filter((r) => r.id === reportId).reduce((R, r) => r, null);
      if (!favorites.includes(reportId)) {
        onAdd()
          .then(() => {
            toast.info(`Звіт ${curRep.name} додано до улюблених`, {
              theme: 'colored',
            });
            setFavorites((o) => [...o, reportId]);
          })
          .catch((e) => setErr(e.message));
      } else {
        onRem()
          .then(() => {
            toast.info(`Звіт ${curRep.name} вилучено з улюблених`, {
              theme: 'colored',
            });
            setFavorites((o) => o.filter((r) => r !== reportId));
          })
          .catch((e) => setErr(e.message));
      }
    },
    [auth, favorites, reports],
  );

  return (
    <>
      <PageHeader
        title="Звіти"
        description="Звіти"
        className="mb-3"
      >
        <div className="ms-2 d-inline">
          <RefreshButton
            onClick={loadReports}
            content=""
          />
        </div>
        <div className="position-absolute" style={{ top: '.2rem', right: '1rem' }}>
          <FalconCloseButton
            onClick={() => dellComponentFromWindowsManager()}
            title="Закрити"
          />
        </div>
      </PageHeader>
      <SearchInput onChange={onSearchHandler} value={search} />
      {loading && <Loader />}
      {err && (
        <IconAlert variant="danger">
          {err}
          {' '}
        </IconAlert>
      )}
      <Container fluid>
        <Row>
          {!schemaId && (
          <Col md={2}>
            <h5 className="text-primary mt-2">
              Категорії звітів
            </h5>
            <Labels
              currentLabel={currentLabel}
              onLabelSelect={setCurrentLabel}
              labels={displayLabels}
            />
          </Col>
          )}
          <Col md={schemaId ? 12 : 10}>
            <ReportsList
              reports={displayReport2}
              favorites={favorites}
              onToggleFavorites={onToggleFavorites}
              showChapters={currentLabel !== FAVORITES_LABEL_ID || !!schemaId}
            />

          </Col>
        </Row>
      </Container>
    </>
  );
}

export default ReportsPanel;

ReportsPanel.propTypes = {
  schemaId: PropTypes.string,
};

ReportsPanel.defaultProps = {
  schemaId: '',
};
